/**
 * 物流追踪弹窗组件
 * 用于显示物流轨迹信息
 */

import React, { useState, useEffect } from 'react';
import { Modal, ModalContainer, Overlay } from '../ModalContainer';
import Button from '../shared/Button';
import { getTracking } from '../../utils/Apis/shipping';

const TrackingModal = ({ isOpen, onClose, trackingNumber, orderId }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const trackingItems =
    trackingData?.trackingList ||
    trackingData?.events ||
    trackingData?.tracking_details ||
    [];

  useEffect(() => {
    if (isOpen && trackingNumber) {
      handleTrack();
    }
  }, [isOpen, trackingNumber]);

  const handleTrack = async () => {
    if (!trackingNumber) return;
    
    setLoading(true);
    setError(null);
    try {
      const result = await getTracking(trackingNumber);
      setTrackingData(result.tracking);
    } catch (err) {
      setError(err.message || '获取物流信息失败');
      console.error('Failed to get tracking', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalContainer>
      <Overlay onClick={onClose} />
      <Modal className="w-full max-w-lg bg-background rounded-lg shadow-xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-foreground">物流追踪</h2>
          
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
            <p className="text-sm text-gray-600 dark:text-gray-400">追踪号码</p>
            <p className="font-mono font-medium text-foreground">{trackingNumber}</p>
          </div>

          {orderId && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
              <p className="text-sm text-gray-600 dark:text-gray-400">订单ID</p>
              <p className="font-mono text-sm text-foreground">{orderId}</p>
            </div>
          )}

          {trackingData && (
            <div className="mb-4 grid gap-3 md:grid-cols-2">
              <div className="rounded bg-gray-50 p-3 dark:bg-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">Destination Country</p>
                <p className="font-medium text-foreground">{trackingData.destinationCountry || '-'}</p>
              </div>
              <div className="rounded bg-gray-50 p-3 dark:bg-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">Carrier</p>
                <p className="font-medium text-foreground">{trackingData.serverName || '-'}</p>
              </div>
              <div className="rounded bg-gray-50 p-3 dark:bg-gray-800 md:col-span-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">Carrier Order No.</p>
                <p className="font-mono text-sm text-foreground">{trackingData.serverNum || '-'}</p>
              </div>
            </div>
          )}

          <Button 
            onClick={handleTrack} 
            disabled={loading} 
            variant="outline"
            className="mb-4"
          >
            {loading ? '加载中...' : '刷新追踪信息'}
          </Button>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900 rounded">
              <p className="text-red-600 dark:text-red-300 text-sm">{error}</p>
            </div>
          )}

          {trackingData && (
            <div className="max-h-80 overflow-y-auto">
              {trackingItems.length > 0 ? (
                <div className="space-y-4">
                  {trackingItems.map((event, index) => (
                    <div key={index} className="relative pl-6 border-l-2 border-blue-500">
                      <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-500 rounded-full"></div>
                      <div className="mb-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {event.occurDatetime || event.time || event.timestamp || event.date}
                        </p>
                      </div>
                      <p className="font-medium text-foreground">
                        {event.trackingContent || event.status || event.event || event.description}
                      </p>
                      {(event.occurLocation || event.location || event.city || event.country) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {event.occurLocation || event.location || [event.city, event.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {(event.businessLinkCode || event.timeZone) && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {[event.businessLinkCode, event.timeZone].filter(Boolean).join(' | ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">暂无物流轨迹信息</p>
              )}
            </div>
          )}

          {!trackingData && !error && !loading && (
            <p className="text-center text-gray-500 py-4">点击"刷新追踪信息"获取物流轨迹</p>
          )}

          <div className="flex justify-end mt-6">
            <Button onClick={onClose}>
              关闭
            </Button>
          </div>
        </div>
      </Modal>
    </ModalContainer>
  );
};

export default TrackingModal;
